/**
 * AI-driven template library (Jun 11, /appv3 Product Shot route).
 *
 * Each entry is a (display metadata + sample-image path) tuple. The
 * matching prompt lives backend-side in `backend/ai_templates.py` —
 * the frontend never sees the prompt text. At render time the frontend
 * sends `aiTemplateId` and the backend looks up the prompt and uses
 * it as the visual_direction in the AI Magic render path.
 *
 * Sample images at `frontend/public/ai-templates/product-shot/<id>.webp`.
 * The Jun 11 set (10 templates below) uses externally rendered
 * campaign-grade samples (luxury moisturizer placeholder product),
 * center-cropped to 1024×1024 WebP q90 (~85KB avg — sized so the
 * /templates browse tiles stay crisp on retina at ~400 CSS px wide).
 * The full-res source PNGs live at
 * `frontend/src/assets-src/ai-templates/product-shot/` (kept OUT of
 * public/ so they don't ship to Firebase Hosting) — regenerate the
 * WebPs from them if the crop/size ever changes. Batch 1 (skincare):
 * 1.png–10.png map to the first 10 entries in order. Batch 2
 * (sparkling water): SparklingWater*.png file numbers do NOT match
 * prompt order — the verified mapping is -4→chilled-hero, -3→golden-sip,
 * 1→beach-day, 2→on-ice, -5→splash-burst, -6→cooler-pull,
 * -7→city-stroll, -8→tropical-spread, -9→tone-match. To add a new
 * template:
 *   1. Add the prompt + metadata to PRODUCT_SHOT_AI_TEMPLATES in
 *      backend/ai_templates.py
 *   2. Supply a sample WebP (or render one via
 *      `python scripts/gen_ai_templates.py --only <id>`)
 *   3. Append the same id + display metadata here (with a `domain` tag
 *      so it lands in the right "Recommended" group)
 */

import customTemplatesData from './ai-templates-custom.json'

/** Product-domain grouping (Jun 11 — /appv3 Templates UX). Drives the
 *  "Recommended for <domain>" section on the Templates tab: when the
 *  picked product's metadata maps to a domain, matching templates
 *  surface first with the rest behind a "View all" expander. Distinct
 *  from `category` (visual style) — this is WHAT KIND of product the
 *  template's scene was authored around. */
export type AiTemplateDomain = 'skincare' | 'consumable' | 'wearable'

export const AI_TEMPLATE_DOMAIN_LABELS: Record<AiTemplateDomain, string> = {
  skincare: 'Skincare',
  consumable: 'Consumables',
  wearable: 'Wearables',
}

/** Map a product's cached classification (productCategory + tags from
 *  analyze-product / scrape-product — the 13-category vocab) onto a
 *  template domain. Returns null when the product doesn't clearly fit
 *  any domain (tech / home / other / no metadata) — the Templates tab
 *  then shows the flat ungrouped library. */
export function domainForProductMeta(
  category?: string | null,
  tags?: readonly string[] | null,
): AiTemplateDomain | null {
  const cat = (category ?? '').toLowerCase()
  // Jul 13 — kept in sync with the backend classifier vocab + product_type
  // families. Used for fashionProductMismatch + the "Recommended for you"
  // grouping (NOT the recipe filter — that's server-side now).
  if (['beauty', 'skincare', 'makeup', 'face-makeup', 'eye-makeup', 'lip-makeup', 'haircare', 'fragrance', 'wellness', 'candle'].includes(cat)) return 'skincare'
  if (['food', 'beverage', 'supplement', 'tea-coffee', 'sports-drink', 'workout-supplement'].includes(cat)) return 'consumable'
  if ([
    'fashion', 'apparel', 'footwear', 'shirts', 'pants', 'dresses', 'activewear',
    'coats', 'sneakers', 'boots',
    // Jul 20 — granular footwear leaves (all worn on the feet → wearable).
    'flats', 'heels', 'sandals',
    'accessory', 'jewelry',
    'bags', 'wallets', 'sunglasses', 'hats', 'belts', 'watches', 'scarves',
  ].includes(cat)) return 'wearable'
  // 'none' (No Recipe) has no domain → returns null → Templates tab shows the
  // flat/ungrouped library + the Fashion gate stays permissive. Intentional.
  // Tag fallback for 'other' / missing category — the vibe-tag vocab
  // overlaps the category names for the big three verticals.
  const tagSet = new Set((tags ?? []).map((t) => t.toLowerCase()))
  if (['skincare', 'beauty', 'fragrance', 'spa', 'wellness', 'candle'].some((t) => tagSet.has(t))) return 'skincare'
  if (['food', 'beverage'].some((t) => tagSet.has(t))) return 'consumable'
  if (['fashion', 'athletic'].some((t) => tagSet.has(t))) return 'wearable'
  return null
}

/** Sentinel "template" id for the AI Magic freestyle tile (Jun 12) — the
 *  first tile in /appv3's Templates grid. NOT a real template: selecting
 *  it generates from product metadata alone (concept LLM, no template
 *  prompt, no pattern dispatch). Lives here so any surface rendering the
 *  template library can special-case it without circular imports. */
export const AI_FREESTYLE_TEMPLATE_ID = 'ai-freestyle'

export interface AiTemplate {
  id: string
  name: string
  category: 'model' | 'studio' | 'ingredient' | 'contextual' | 'lifestyle'
  /** Which wizard route the template belongs to. The 3 model-led
   *  templates show a real human HOLDING the product, so they live on
   *  the In Hand route ('product-swap'); the rest are pure product
   *  photography on Product Shot. Drives category filtering on
   *  /templates + which /appv3 route's Templates tab surfaces them +
   *  the `route` sent to /api/wizard/ai-generate. */
  route: 'product-shot' | 'product-swap'
  /** Product-domain tag — optional so older / hand-edited custom JSON
   *  entries without it still load (they just only appear under
   *  "View all", never in a recommended group). */
  domain?: AiTemplateDomain
}

// Built-in library (hand-ingested batches). Custom templates authored
// via the /template-lab dev page land in ai-templates-custom.json and
// merge in below — same shape, appended after the built-ins.
const BUILTIN_AI_TEMPLATES: readonly AiTemplate[] = [
  // ---- In Hand (model holding the product) ----
  { id: 'editorial-luxe',   name: 'Editorial Luxe',    category: 'model',      route: 'product-swap', domain: 'skincare' },
  { id: 'candid-glow',      name: 'Candid Glow',       category: 'model',      route: 'product-swap', domain: 'skincare' },
  { id: 'playful-pastel',   name: 'Playful Pastel',    category: 'model',      route: 'product-swap', domain: 'skincare' },
  // ---- Product Shot (no human) ----
  { id: 'lavender-hero',    name: 'Lavender Hero',     category: 'studio',     route: 'product-shot', domain: 'skincare' },
  { id: 'still-water',      name: 'Still Water',       category: 'studio',     route: 'product-shot', domain: 'skincare' },
  { id: 'golden-splash',    name: 'Golden Splash',     category: 'studio',     route: 'product-shot', domain: 'skincare' },
  { id: 'hydration-gels',   name: 'Hydration Gels',    category: 'studio',     route: 'product-shot', domain: 'skincare' },
  { id: 'cinematic-liquid', name: 'Cinematic Liquid',  category: 'studio',     route: 'product-shot', domain: 'skincare' },
  { id: 'citrus-fresh',     name: 'Citrus Fresh',      category: 'ingredient', route: 'product-shot', domain: 'skincare' },
  { id: 'succulent-nest',   name: 'Succulent Nest',    category: 'contextual', route: 'product-shot', domain: 'skincare' },
  // ---- Sparkling Water / Consumable set (Jun 11, batch 2) ----
  // In Hand (model / hands holding the product)
  { id: 'golden-sip',       name: 'Golden Sip',        category: 'model',      route: 'product-swap', domain: 'consumable' },
  { id: 'beach-day',        name: 'Beach Day',         category: 'model',      route: 'product-swap', domain: 'consumable' },
  { id: 'cooler-pull',      name: 'Cooler Pull',       category: 'lifestyle',  route: 'product-swap', domain: 'consumable' },
  { id: 'city-stroll',      name: 'City Stroll',       category: 'model',      route: 'product-swap', domain: 'consumable' },
  // Product Shot (no human)
  { id: 'chilled-hero',     name: 'Chilled Hero',      category: 'studio',     route: 'product-shot', domain: 'consumable' },
  { id: 'on-ice',           name: 'On Ice',            category: 'studio',     route: 'product-shot', domain: 'consumable' },
  { id: 'splash-burst',     name: 'Splash Burst',      category: 'studio',     route: 'product-shot', domain: 'consumable' },
  { id: 'tropical-spread',  name: 'Tropical Spread',   category: 'ingredient', route: 'product-shot', domain: 'consumable' },
  { id: 'tone-match',       name: 'Tone Match',        category: 'studio',     route: 'product-shot', domain: 'consumable' },
] as const

// Defensive shape filter — the JSON is written by the Template Lab's
// Vite middleware so it should always be valid, but a hand-edit typo
// shouldn't blank the whole library.
const CUSTOM_AI_TEMPLATES: AiTemplate[] = (customTemplatesData as AiTemplate[])
  .filter((t) => t && typeof t.id === 'string' && typeof t.name === 'string')

export const PRODUCT_SHOT_AI_TEMPLATES: readonly AiTemplate[] = [
  ...BUILTIN_AI_TEMPLATES,
  ...CUSTOM_AI_TEMPLATES,
]

/** Templates for one wizard route — /appv3's Templates tab shows only
 *  the active route's subset. */
export function aiTemplatesForRoute(
  route: AiTemplate['route'],
): readonly AiTemplate[] {
  return PRODUCT_SHOT_AI_TEMPLATES.filter((t) => t.route === route)
}

/** Templates shown in the MERGED Product Shot route (Jun 12 v6 — In Hand
 *  folded into Product Shot). Product Shot now surfaces BOTH product-only
 *  AND held (In Hand) templates; each carries its own `route`, so the
 *  generation dispatches per-template (held → product-swap backend +
 *  safety scaffold). Ordered product-only first, held after. */
export function aiTemplatesForStudioRoute(
  category: string,
): readonly AiTemplate[] {
  if (category === 'product-shot' || category === 'product-swap') {
    const productOnly = PRODUCT_SHOT_AI_TEMPLATES.filter((t) => t.route === 'product-shot')
    const held = PRODUCT_SHOT_AI_TEMPLATES.filter((t) => t.route === 'product-swap')
    return [...productOnly, ...held]
  }
  return aiTemplatesForRoute(category as AiTemplate['route'])
}

/** Path to the pre-rendered sample image for a template. Lives in
 *  `frontend/public/ai-templates/product-shot/` so it's served as a
 *  static asset (no build step). */
export function aiTemplateSamplePath(t: AiTemplate): string {
  return `/ai-templates/product-shot/${t.id}.webp`
}
